import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import type { InterviewType } from "../graphql/generated/graphql";
import {
  CreateTermMutation,
  DeleteTermMutation,
  TermQuery,
  TermsQuery,
  UpdateTermMutation,
} from "../graphql/terms.graphql";

export interface Term {
  termId: string;
  sponsorId: string;
  interviewType: InterviewType;
  title: string;
  durationMinutes: number;
  description: string | null;
  notionUrl: string | null;
  summaryDueDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateTermInput {
  sponsorId: string;
  title: string;
  interviewType: InterviewType;
  description?: string | null;
  notionUrl?: string | null;
  summaryDueDate?: string | null;
  durationMinutes?: number | null;
}

export interface UpdateTermInput {
  termId: string;
  title?: string | null;
  description?: string | null;
  summaryDueDate?: string | null;
}

export function useTerms(sponsorId: string | null) {
  const [result, reexecute] = useQuery({
    query: TermsQuery,
    variables: { sponsorId: sponsorId ?? "" },
    pause: !sponsorId,
  });

  return {
    terms: result.data?.terms.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTerm(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: TermQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    term: result.data?.term ?? null,
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTermMutations() {
  const [, executeCreateTerm] = useMutation(CreateTermMutation);
  const [, executeUpdateTerm] = useMutation(UpdateTermMutation);
  const [, executeDeleteTerm] = useMutation(DeleteTermMutation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTerm = useCallback(
    async (data: CreateTermInput): Promise<Term | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeCreateTerm({ input: data });
        if (result.error) throw new Error(result.error.message);
        return result.data?.createTerm ?? null;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "タームの作成に失敗しました",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeCreateTerm],
  );

  const updateTerm = useCallback(
    async (data: UpdateTermInput): Promise<Term | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeUpdateTerm({ input: data });
        if (result.error) throw new Error(result.error.message);
        return result.data?.updateTerm ?? null;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "タームの更新に失敗しました",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdateTerm],
  );

  const deleteTerm = useCallback(
    async (termId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await executeDeleteTerm({ termId });
        if (result.error) throw new Error(result.error.message);
        return result.data?.deleteTerm ?? false;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "タームの削除に失敗しました",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [executeDeleteTerm],
  );

  return { createTerm, updateTerm, deleteTerm, loading, error };
}
